import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faChartLine,
  faDollarSign,
  faMoneyBillWave,
  faGlobe,
  faSquare,
  faBook,
  faSave,
  faArrowsRotate,
  faRepeat,
} from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { JOURNAL_ROUTES } from '@/constants/routes';
import { formatCurrencyCentValue, formatPercentage, toCurrencyCentValue } from '@/utils/helpers';
import { showToast, handleAPIError } from '@/utils/toasts';
import type { JournalSettings, JournalStrategy, JournalReviewCadence } from '@/models';
import type { UpdateJournalSettingsRequest } from '@/modelsAPI';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import ErrorState from '@/components/ui/ErrorState';
import {
  JOURNAL_COLOR_REGEX,
  JOURNAL_FIELD_CLASS,
  JOURNAL_LABEL_CLASS,
  STRATEGY_LABELS,
  readableTextColor,
} from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';

const INPUT_CLASS = `${JOURNAL_FIELD_CLASS} w-full disabled:opacity-60`;
const INVALID_INPUT_CLASS = `${INPUT_CLASS} border-red-500 dark:border-red-500`;
const ERROR_TEXT_CLASS = 'mt-1 text-sm text-red-600 dark:text-red-400';
const HINT_TEXT_CLASS = 'mt-1 text-xs text-gray-500 dark:text-gray-400';
const CARD_HINT_CLASS = 'text-sm text-gray-500 dark:text-gray-400 mb-4';

const CURRENCY_REGEX = /^[A-Z]{3}$/;

// The app only ever prices in these two - IB accounts of the community trade US options.
const CURRENCY_OPTIONS: string[] = ['USD', 'EUR'];

const STRATEGY_OPTIONS: JournalStrategy[] = ['KERNSTRATEGIE', 'REPARATURSTRATEGIE', 'EARNING_TRADE', 'UNKNOWN'];

const CADENCE_OPTIONS: JournalReviewCadence[] = ['OFF', 'WEEKLY', 'MONTHLY', 'BOTH'];
const CADENCE_LABELS: Record<JournalReviewCadence, string> = {
  OFF: 'Aus',
  WEEKLY: 'Wöchentlich',
  MONTHLY: 'Monatlich',
  BOTH: 'Wöchentlich + monatlich',
};

const WEEKDAY_LABELS: string[] = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

// models.NewJournalSettings() in the backend.
const DEFAULT_COLORS = {
  core: '#10b981',
  repair: '#0433ff',
  earning: '#0c7a55',
} as const;

const FALLBACK_TIMEZONES: string[] = [
  'Europe/Berlin',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/London',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'UTC',
];

function supportedTimezones(): string[] {
  if (typeof Intl.supportedValuesOf !== 'function') return FALLBACK_TIMEZONES;
  try {
    const values = Intl.supportedValuesOf('timeZone');
    return values.length > 0 ? values : FALLBACK_TIMEZONES;
  } catch {
    return FALLBACK_TIMEZONES;
  }
}

interface SettingsForm {
  defaultCurrency: string;
  defaultMultiplier: string;
  defaultStrategy: JournalStrategy;
  defaultOpenFee: string;
  defaultCloseFee: string;
  accountSize: string;
  timezone: string;
  strategyColorCore: string;
  strategyColorRepair: string;
  strategyColorEarning: string;
  reviewCadence: JournalReviewCadence;
  reviewWeekday: string;
}

type FormErrors = Partial<Record<keyof SettingsForm, string>>;

function centsToInput(cents: number): string {
  return (cents / 100).toString();
}

/** Returns null for empty or non-numeric input; accepts a German decimal comma. */
function parseAmount(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (normalized === '') return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

function toForm(settings: JournalSettings): SettingsForm {
  return {
    defaultCurrency: settings.defaultCurrency,
    defaultMultiplier: settings.defaultMultiplier.toString(),
    defaultStrategy: settings.defaultStrategy,
    defaultOpenFee: centsToInput(settings.defaultOpenFee),
    defaultCloseFee: centsToInput(settings.defaultCloseFee),
    accountSize: centsToInput(settings.accountSize),
    timezone: settings.timezone,
    strategyColorCore: settings.strategyColorCore,
    strategyColorRepair: settings.strategyColorRepair,
    strategyColorEarning: settings.strategyColorEarning,
    reviewCadence: settings.reviewCadence,
    reviewWeekday: settings.reviewWeekday.toString(),
  };
}

interface FormAnalysis {
  errors: FormErrors;
  patch: UpdateJournalSettingsRequest;
}

/**
 * Mirrors the validation of api/handler/journalSettings.go so an invalid value never
 * leaves the browser, and collects the changed fields for the PATCH body.
 */
function analyzeForm(form: SettingsForm, baseline: JournalSettings, timezones: readonly string[]): FormAnalysis {
  const errors: FormErrors = {};
  const patch: UpdateJournalSettingsRequest = {};

  const currency = form.defaultCurrency.trim().toUpperCase();
  if (!CURRENCY_REGEX.test(currency)) {
    errors.defaultCurrency = 'Bitte einen dreistelligen Währungscode wählen.';
  } else if (currency !== baseline.defaultCurrency) {
    patch.defaultCurrency = currency;
  }

  const multiplier = parseAmount(form.defaultMultiplier);
  if (multiplier === null || !Number.isInteger(multiplier) || multiplier <= 0) {
    errors.defaultMultiplier = 'Bitte eine ganze Zahl größer als 0 eingeben.';
  } else if (multiplier !== baseline.defaultMultiplier) {
    patch.defaultMultiplier = multiplier;
  }

  if (!STRATEGY_OPTIONS.includes(form.defaultStrategy)) {
    errors.defaultStrategy = 'Unbekannte Strategie.';
  } else if (form.defaultStrategy !== baseline.defaultStrategy) {
    patch.defaultStrategy = form.defaultStrategy;
  }

  const openFee = parseAmount(form.defaultOpenFee);
  if (openFee === null || openFee < 0) {
    errors.defaultOpenFee = 'Bitte einen Betrag von 0 oder mehr eingeben.';
  } else if (toCurrencyCentValue(openFee) !== baseline.defaultOpenFee) {
    patch.defaultOpenFee = toCurrencyCentValue(openFee);
  }

  const closeFee = parseAmount(form.defaultCloseFee);
  if (closeFee === null || closeFee < 0) {
    errors.defaultCloseFee = 'Bitte einen Betrag von 0 oder mehr eingeben.';
  } else if (toCurrencyCentValue(closeFee) !== baseline.defaultCloseFee) {
    patch.defaultCloseFee = toCurrencyCentValue(closeFee);
  }

  const accountSize = parseAmount(form.accountSize);
  if (accountSize === null || accountSize < 0) {
    errors.accountSize = 'Bitte einen Betrag von 0 oder mehr eingeben (0 = nicht gesetzt).';
  } else if (toCurrencyCentValue(accountSize) !== baseline.accountSize) {
    patch.accountSize = toCurrencyCentValue(accountSize);
  }

  const timezone = form.timezone.trim();
  if (!timezones.includes(timezone)) {
    errors.timezone = 'Unbekannte Zeitzone. Bitte einen Eintrag aus der Vorschlagsliste wählen.';
  } else if (timezone !== baseline.timezone) {
    patch.timezone = timezone;
  }

  if (!JOURNAL_COLOR_REGEX.test(form.strategyColorCore)) {
    errors.strategyColorCore = 'Bitte einen Hex-Farbwert wie #10b981 eingeben.';
  } else if (form.strategyColorCore !== baseline.strategyColorCore) {
    patch.strategyColorCore = form.strategyColorCore;
  }

  if (!JOURNAL_COLOR_REGEX.test(form.strategyColorRepair)) {
    errors.strategyColorRepair = 'Bitte einen Hex-Farbwert wie #0433ff eingeben.';
  } else if (form.strategyColorRepair !== baseline.strategyColorRepair) {
    patch.strategyColorRepair = form.strategyColorRepair;
  }

  if (!JOURNAL_COLOR_REGEX.test(form.strategyColorEarning)) {
    errors.strategyColorEarning = 'Bitte einen Hex-Farbwert wie #0c7a55 eingeben.';
  } else if (form.strategyColorEarning !== baseline.strategyColorEarning) {
    patch.strategyColorEarning = form.strategyColorEarning;
  }

  if (!CADENCE_OPTIONS.includes(form.reviewCadence)) {
    errors.reviewCadence = 'Unbekannter Rhythmus.';
  } else if (form.reviewCadence !== baseline.reviewCadence) {
    patch.reviewCadence = form.reviewCadence;
  }

  const weekday = parseAmount(form.reviewWeekday);
  if (weekday === null || !Number.isInteger(weekday) || weekday < 1 || weekday > 7) {
    errors.reviewWeekday = 'Bitte einen Wochentag von Montag (1) bis Sonntag (7) wählen.';
  } else if (weekday !== baseline.reviewWeekday) {
    patch.reviewWeekday = weekday;
  }

  return { errors, patch };
}

function inputClassFor(error: string | undefined): string {
  return error === undefined ? INPUT_CLASS : INVALID_INPUT_CLASS;
}

interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

function Field({ id, label, hint, error, children }: FieldProps) {
  return (
    <div>
      <label className={JOURNAL_LABEL_CLASS} htmlFor={id}>{label}</label>
      {children}
      {error !== undefined ? (
        <p className={ERROR_TEXT_CLASS}>{error}</p>
      ) : (
        hint !== undefined && <p className={HINT_TEXT_CLASS}>{hint}</p>
      )}
    </div>
  );
}

interface ColorFieldProps {
  id: string;
  label: string;
  previewLabel: string;
  value: string;
  error?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

function ColorField({ id, label, previewLabel, value, error, disabled, onChange }: ColorFieldProps) {
  const valid = JOURNAL_COLOR_REGEX.test(value);
  return (
    <div>
      <label className={JOURNAL_LABEL_CLASS} htmlFor={id}>{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`Farbe für ${previewLabel} auswählen`}
          className="h-10 w-14 shrink-0 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer disabled:opacity-60"
          value={valid ? value.toLowerCase() : '#000000'}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          id={id}
          type="text"
          inputMode="text"
          spellCheck={false}
          className={inputClassFor(error)}
          value={value}
          placeholder="#10b981"
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      <div className="mt-2">
        <Badge
          colorClass={valid ? '' : undefined}
          style={valid ? { backgroundColor: value, color: readableTextColor(value) } : undefined}
          color="gray"
        >
          {previewLabel}
        </Badge>
      </div>
      {error !== undefined && <p className={ERROR_TEXT_CLASS}>{error}</p>}
    </div>
  );
}

export default function JournalSettingsPage() {
  const { data, loading, error, reload } = useJournalResource(
    () => APIRoutes.getJournalSettings(),
    [],
    'Fehler beim Laden der Journal-Einstellungen',
  );

  // Only feeds the utilisation preview - a failure here must never block the form.
  const { data: stats } = useJournalResource(
    () => APIRoutes.getJournalStats(),
    [],
    'Fehler beim Laden der Journal-Statistik',
  );

  const [baseline, setBaseline] = useState<JournalSettings | null>(null);
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data === null) return;
    setBaseline(data.settings);
    setForm(toForm(data.settings));
  }, [data]);

  const timezones = useMemo(() => {
    const base = supportedTimezones();
    // A timezone already stored on the account must never be rejected by our own list.
    if (baseline !== null && baseline.timezone !== '' && !base.includes(baseline.timezone)) {
      return [...base, baseline.timezone].sort((a, b) => a.localeCompare(b, 'de-DE'));
    }
    return base;
  }, [baseline]);

  const analysis = useMemo<FormAnalysis>(() => {
    if (form === null || baseline === null) return { errors: {}, patch: {} };
    return analyzeForm(form, baseline, timezones);
  }, [form, baseline, timezones]);

  const errors = analysis.errors;
  const errorCount = Object.keys(errors).length;
  const changeCount = Object.keys(analysis.patch).length;
  const dirty = changeCount > 0 || errorCount > 0;

  const setField = <K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) => {
    setForm((prev) => (prev === null ? prev : { ...prev, [key]: value }));
  };

  const handleReset = () => {
    if (baseline === null) return;
    setForm(toForm(baseline));
  };

  const handleResetColors = () => {
    setForm((prev) =>
      prev === null
        ? prev
        : {
            ...prev,
            strategyColorCore: DEFAULT_COLORS.core,
            strategyColorRepair: DEFAULT_COLORS.repair,
            strategyColorEarning: DEFAULT_COLORS.earning,
          },
    );
  };

  const handleSave = async () => {
    if (errorCount > 0 || changeCount === 0) return;
    setSaving(true);
    try {
      const response = await APIRoutes.updateJournalSettings(analysis.patch);
      setBaseline(response.settings);
      setForm(toForm(response.settings));
      showToast('Einstellungen gespeichert');
    } catch (err) {
      handleAPIError(err, 'Einstellungen konnten nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  if (loading || form === null || baseline === null) {
    return (
      <div className="text-center py-16" role="status" aria-label="Lädt">
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  const currency = CURRENCY_REGEX.test(form.defaultCurrency.trim().toUpperCase())
    ? form.defaultCurrency.trim().toUpperCase()
    : 'USD';
  const currencyOptions = CURRENCY_OPTIONS.includes(baseline.defaultCurrency)
    ? CURRENCY_OPTIONS
    : [...CURRENCY_OPTIONS, baseline.defaultCurrency];

  const accountSizeValue = parseAmount(form.accountSize);
  const accountSizeCents = accountSizeValue !== null && accountSizeValue > 0 ? toCurrencyCentValue(accountSizeValue) : 0;
  const utilization = stats !== null && accountSizeCents > 0 ? stats.capitalAtRisk / accountSizeCents : null;

  return (
    <div className="space-y-4">
      <Card title="Handel" icon={faChartLine}>
        <p className={CARD_HINT_CLASS}>
          Diese drei Werte sind im Formular für neue Trades vorbelegt.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field
            id="settings-currency"
            label="Währung"
            hint="Währung, in der neue Trades erfasst werden."
            error={errors.defaultCurrency}
          >
            <select
              id="settings-currency"
              className={`${inputClassFor(errors.defaultCurrency)} cursor-pointer`}
              value={form.defaultCurrency}
              disabled={saving}
              onChange={(e) => setField('defaultCurrency', e.target.value)}
            >
              {currencyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>

          <Field
            id="settings-multiplier"
            label="Kontraktgröße"
            hint="Aktien pro Optionskontrakt, üblicherweise 100."
            error={errors.defaultMultiplier}
          >
            <input
              id="settings-multiplier"
              type="number"
              min={1}
              step={1}
              className={inputClassFor(errors.defaultMultiplier)}
              value={form.defaultMultiplier}
              disabled={saving}
              onChange={(e) => setField('defaultMultiplier', e.target.value)}
            />
          </Field>

          <Field
            id="settings-strategy"
            label="Strategie"
            hint="Vorausgewählte Strategie neuer Trades."
            error={errors.defaultStrategy}
          >
            <select
              id="settings-strategy"
              className={`${inputClassFor(errors.defaultStrategy)} cursor-pointer`}
              value={form.defaultStrategy}
              disabled={saving}
              onChange={(e) => setField('defaultStrategy', e.target.value as JournalStrategy)}
            >
              {STRATEGY_OPTIONS.map((s) => (
                <option key={s} value={s}>{STRATEGY_LABELS[s]}</option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      <Card title="Gebühren" icon={faDollarSign}>
        <p className={CARD_HINT_CLASS}>
          Werden beim Eröffnen und beim Schließen eines Trades vorbelegt. Die Optionsgebühren von
          Interactive Brokers sind in der Praxis konstant, deshalb lohnt sich ein fester Wert.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            id="settings-open-fee"
            label={`Gebühr beim Eröffnen (${currency})`}
            hint="Pro Trade, nicht pro Kontrakt."
            error={errors.defaultOpenFee}
          >
            <input
              id="settings-open-fee"
              type="number"
              min={0}
              step={0.01}
              className={inputClassFor(errors.defaultOpenFee)}
              value={form.defaultOpenFee}
              disabled={saving}
              onChange={(e) => setField('defaultOpenFee', e.target.value)}
            />
          </Field>

          <Field
            id="settings-close-fee"
            label={`Gebühr beim Schließen (${currency})`}
            hint="Pro Trade, nicht pro Kontrakt."
            error={errors.defaultCloseFee}
          >
            <input
              id="settings-close-fee"
              type="number"
              min={0}
              step={0.01}
              className={inputClassFor(errors.defaultCloseFee)}
              value={form.defaultCloseFee}
              disabled={saving}
              onChange={(e) => setField('defaultCloseFee', e.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card title="Kapital" icon={faMoneyBillWave}>
        <p className={CARD_HINT_CLASS}>
          Mit einer Kontogröße wird aus dem absoluten gebundenen Kapital im Dashboard und in der
          Kapitalleiter eine Auslastung in Prozent. 0 bedeutet: nicht gesetzt.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            id="settings-account-size"
            label={`Kontogröße (${currency})`}
            hint="0 = keine Auslastung berechnen."
            error={errors.accountSize}
          >
            <input
              id="settings-account-size"
              type="number"
              min={0}
              step={0.01}
              className={inputClassFor(errors.accountSize)}
              value={form.accountSize}
              disabled={saving}
              onChange={(e) => setField('accountSize', e.target.value)}
            />
          </Field>

          {utilization !== null && stats !== null && (
            <div className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Aktuelle Auslastung</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {formatPercentage(utilization)}
              </p>
              <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
                <div
                  className={`h-full rounded-full ${utilization > 1 ? 'bg-red-500' : 'bg-blue-500'}`}
                  style={{ width: `${Math.min(Math.max(utilization, 0), 1) * 100}%` }}
                />
              </div>
              <p className={HINT_TEXT_CLASS}>
                {formatCurrencyCentValue(stats.capitalAtRisk, currency)} gebundenes Kapital von{' '}
                {formatCurrencyCentValue(accountSizeCents, currency)}
              </p>
            </div>
          )}
        </div>
      </Card>

      <Card title="Zeitzone" icon={faGlobe}>
        <p className={CARD_HINT_CLASS}>
          Bestimmt, auf welches Datum der nächtliche Job deine geplanten Reviews legt.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            id="settings-timezone"
            label="Zeitzone"
            hint="IANA-Name, z.B. Europe/Berlin oder America/New_York."
            error={errors.timezone}
          >
            <input
              id="settings-timezone"
              type="text"
              list="settings-timezone-list"
              spellCheck={false}
              className={inputClassFor(errors.timezone)}
              value={form.timezone}
              disabled={saving}
              onChange={(e) => setField('timezone', e.target.value)}
            />
            <datalist id="settings-timezone-list">
              {timezones.map((tz) => (
                <option key={tz} value={tz} />
              ))}
            </datalist>
          </Field>
        </div>
      </Card>

      <Card
        title="Strategie-Farben"
        icon={faSquare}
        actions={
          <Button variant="secondary" size="sm" icon={faRepeat} disabled={saving} onClick={handleResetColors}>
            Zurücksetzen
          </Button>
        }
      >
        <p className={CARD_HINT_CLASS}>
          Die Farben werden gespeichert, aber noch nirgends ausgespielt: die Strategie-Anzeigen in
          Listen und Detailseiten nutzen sie bisher nicht.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ColorField
            id="settings-color-core"
            label="Kernstrategie"
            previewLabel={STRATEGY_LABELS.KERNSTRATEGIE}
            value={form.strategyColorCore}
            error={errors.strategyColorCore}
            disabled={saving}
            onChange={(v) => setField('strategyColorCore', v)}
          />
          <ColorField
            id="settings-color-repair"
            label="Reparaturstrategie"
            previewLabel={STRATEGY_LABELS.REPARATURSTRATEGIE}
            value={form.strategyColorRepair}
            error={errors.strategyColorRepair}
            disabled={saving}
            onChange={(v) => setField('strategyColorRepair', v)}
          />
          <ColorField
            id="settings-color-earning"
            label="Earning Trade"
            previewLabel={STRATEGY_LABELS.EARNING_TRADE}
            value={form.strategyColorEarning}
            error={errors.strategyColorEarning}
            disabled={saving}
            onChange={(v) => setField('strategyColorEarning', v)}
          />
        </div>
      </Card>

      <Card title="Reviews" icon={faBook}>
        <p className={CARD_HINT_CLASS}>
          Ein nächtlicher Job legt die fälligen Reviews automatisch an. Sie erscheinen dann unter{' '}
          <Link to={JOURNAL_ROUTES.REVIEWS} className="text-blue-600 dark:text-blue-400 hover:underline">
            Reviews
          </Link>
          .
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            id="settings-review-cadence"
            label="Rhythmus"
            hint="Aus bedeutet: es wird nichts automatisch angelegt."
            error={errors.reviewCadence}
          >
            <select
              id="settings-review-cadence"
              className={`${inputClassFor(errors.reviewCadence)} cursor-pointer`}
              value={form.reviewCadence}
              disabled={saving}
              onChange={(e) => setField('reviewCadence', e.target.value as JournalReviewCadence)}
            >
              {CADENCE_OPTIONS.map((c) => (
                <option key={c} value={c}>{CADENCE_LABELS[c]}</option>
              ))}
            </select>
          </Field>

          <Field
            id="settings-review-weekday"
            label="Wochentag"
            hint="Wochenrückblicke landen auf diesem Tag, Monatsrückblicke auf seinem letzten Vorkommen im Monat."
            error={errors.reviewWeekday}
          >
            <select
              id="settings-review-weekday"
              className={`${inputClassFor(errors.reviewWeekday)} cursor-pointer`}
              value={form.reviewWeekday}
              disabled={saving || form.reviewCadence === 'OFF'}
              onChange={(e) => setField('reviewWeekday', e.target.value)}
            >
              {WEEKDAY_LABELS.map((label, index) => (
                <option key={label} value={index + 1}>{label}</option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      {dirty && (
        <div className="sticky bottom-4 z-10">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 backdrop-blur px-4 py-3 shadow-lg">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {errorCount > 0
                ? 'Bitte zuerst die markierten Felder korrigieren.'
                : changeCount === 1
                  ? '1 Änderung noch nicht gespeichert.'
                  : `${changeCount} Änderungen noch nicht gespeichert.`}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="secondary" icon={faArrowsRotate} disabled={saving} onClick={handleReset}>
                Verwerfen
              </Button>
              <Button
                icon={faSave}
                loading={saving}
                disabled={errorCount > 0 || changeCount === 0}
                onClick={() => void handleSave()}
              >
                Speichern
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
